import { FormEvent, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { agent } from '../api/agent';
import { Concept, useSessionStore, sessionSelectors } from '../store/sessionStore';

const conceptWeight = (concept: Concept) => concept.votes + 1;

const hashString = (value: string) =>
  value.split('').reduce((acc, char, index) => acc + char.charCodeAt(0) * (index + 17), 0);

export const ConceptCloud = () => {
  const brief = useSessionStore(sessionSelectors.brief);
  const concepts = useSessionStore(sessionSelectors.concepts);
  const addConcept = useSessionStore((state) => state.addConcept);
  const appendConcepts = useSessionStore((state) => state.appendConcepts);
  const upvoteConcept = useSessionStore((state) => state.upvoteConcept);
  const removeConcept = useSessionStore((state) => state.removeConcept);

  const [newConcept, setNewConcept] = useState('');

  const generateMore = useMutation({
    mutationFn: () => agent.generateConcepts(brief, concepts.map((concept) => concept.label)),
    onSuccess: (data) => {
      appendConcepts(data.concepts);
    }
  });

  const handleAddConcept = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = newConcept.trim();
    if (!trimmed) {
      return;
    }
    addConcept(trimmed, 'manual');
    setNewConcept('');
  };

  const canGenerateMore = Boolean(brief.trim()) && !generateMore.isPending;

  return (
    <div className="panel-section">
      <header className="section-header">
        <div>
          <h2>Concept Word Cloud</h2>
        </div>
        <button
          type="button"
          className="icon-button"
          onClick={() => generateMore.mutate()}
          disabled={!canGenerateMore}
          title={brief ? 'Generate more concepts' : 'Add a brief to generate concepts'}
        >
          {generateMore.isPending ? '…' : '✚✨'}
        </button>
      </header>

      <div className="concept-cloud">
        {concepts.map((concept, index) => {
          const weight = conceptWeight(concept);
          const base = hashString(concept.id);
          const rotation = ((base % 7) - 3) * 2; // -6 to 6 degrees
          const hue = (base * 37) % 360;
          const lightness = 88 - (index % 6) * 4;
          const fontSize = Math.min(30, 16 + weight * 1.6);

          return (
            <div
              key={concept.id}
              className="concept-token"
              style={{
                fontSize,
                transform: `rotate(${rotation}deg)`
              }}
            >
              <button
                type="button"
                className="concept-token-body"
                style={{
                  backgroundColor: `hsla(${hue}, 78%, ${lightness}%, 0.9)`,
                  borderColor: `hsla(${hue}, 70%, ${Math.max(lightness - 20, 35)}%, 0.85)`
                }}
                onClick={() => upvoteConcept(concept.id)}
              >
                <span className="concept-token-icon">▲</span>
                <span className="concept-token-label">{concept.label}</span>
                <span className="concept-token-count">{concept.votes}</span>
              </button>
              <button
                type="button"
                className="concept-token-remove"
                onClick={() => removeConcept(concept.id)}
                aria-label={`Remove concept ${concept.label}`}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>

      <form className="add-concept" onSubmit={handleAddConcept}>
        <input
          type="text"
          placeholder="Add a new concept manually"
          value={newConcept}
          onChange={(event) => setNewConcept(event.target.value)}
        />
        <button type="submit" className="primary-button" disabled={!newConcept.trim()}>
          Add Concept
        </button>
      </form>
    </div>
  );
};
